import { FormEvent, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsDisplay,
  BsPencilSquare,
  BsPlusCircle,
  BsSave,
  BsTrash3,
} from "react-icons/bs";

import InventarioService from "../../../Services/Catalogos/InventarioService";
import { DTOEquipoInventario } from "../../../DTO/Inventario/DTOEquipoInventario";
import { DTOMonitorInventario } from "../../../DTO/Inventario/DTOMonitorInventario";
import { DTOEstadoEquipo } from "../../../DTO/Inventario/DTOCatalogosInventario";
import { DTOGestionarMonitor } from "../../../DTO/Inventario/DTOGestionInventario";

interface MensajeInventario {
  encabezado: string;
  mensaje: string;
  color?: string;
}

interface MonitoresEquipoProps {
  idEquipo: number;
  equipo: DTOEquipoInventario;
  monitores: DTOMonitorInventario[];
  estadosEquipo: DTOEstadoEquipo[];
  onRecargar: () => Promise<void>;
  onMensaje: (mensaje: MensajeInventario) => void;
}

type TipoAsignacionMonitor = "EMPLEADO" | "RESPONSABLE" | "SIN_ASIGNAR";

interface ConfirmacionMonitor {
  id: number;
  descripcion: string;
}

const crearFormularioMonitor = (): DTOGestionarMonitor => ({
  accion: "INSERTAR",
  idEstadoEquipo: null,
  serviceTagSerial: "",
  numEmpleadoAsignado: "",
  nombreResponsable: "",
  idEquipoRelacionado: null,
  observaciones: "",
});

const obtenerMensajeError = (error: unknown): string => {
  const errorAxios = error as {
    response?: { data?: { mensaje?: string } };
    message?: string;
  };

  return (
    errorAxios.response?.data?.mensaje ??
    errorAxios.message ??
    "Ocurrió un error inesperado."
  );
};

const MonitoresEquipo = ({
  idEquipo,
  equipo,
  monitores,
  estadosEquipo,
  onRecargar,
  onMensaje,
}: MonitoresEquipoProps) => {
  const [procesando, setProcesando] = useState(false);
  const [showMonitor, setShowMonitor] = useState(false);
  const [monitorEditar, setMonitorEditar] =
    useState<DTOMonitorInventario | null>(null);
  const [tipoAsignacionMonitor, setTipoAsignacionMonitor] =
    useState<TipoAsignacionMonitor>("SIN_ASIGNAR");
  const [monitorForm, setMonitorForm] =
    useState<DTOGestionarMonitor>(crearFormularioMonitor);
  const [confirmacion, setConfirmacion] =
    useState<ConfirmacionMonitor | null>(null);

  const abrirNuevoMonitor = () => {
    let tipoAsignacion: TipoAsignacionMonitor = "SIN_ASIGNAR";

    if (equipo.numEmpleadoAsignado) {
      tipoAsignacion = "EMPLEADO";
    } else if (equipo.nombreResponsable) {
      tipoAsignacion = "RESPONSABLE";
    }

    setMonitorEditar(null);
    setTipoAsignacionMonitor(tipoAsignacion);
    setMonitorForm({
      accion: "INSERTAR",
      idEstadoEquipo:
        estadosEquipo.find(
          (estado) => estado.estado.toLowerCase() === "activo"
        )?.idEstadoEquipo ?? null,
      serviceTagSerial: "",
      numEmpleadoAsignado:
        tipoAsignacion === "EMPLEADO"
          ? equipo.numEmpleadoAsignado ?? ""
          : "",
      nombreResponsable:
        tipoAsignacion === "RESPONSABLE"
          ? equipo.nombreResponsable ?? ""
          : "",
      idEquipoRelacionado: idEquipo,
      observaciones: "",
    });
    setShowMonitor(true);
  };

  const abrirEditarMonitor = (monitor: DTOMonitorInventario) => {
    let tipoAsignacion: TipoAsignacionMonitor = "SIN_ASIGNAR";

    if (monitor.numEmpleadoAsignado) {
      tipoAsignacion = "EMPLEADO";
    } else if (monitor.nombreResponsable) {
      tipoAsignacion = "RESPONSABLE";
    }

    setMonitorEditar(monitor);
    setTipoAsignacionMonitor(tipoAsignacion);
    setMonitorForm({
      accion: "ACTUALIZAR",
      idMonitor: monitor.idMonitor,
      idEstadoEquipo: monitor.idEstadoEquipo,
      serviceTagSerial: monitor.serviceTagSerial ?? "",
      numEmpleadoAsignado: monitor.numEmpleadoAsignado ?? "",
      nombreResponsable: monitor.nombreResponsable ?? "",
      idEquipoRelacionado: monitor.idEquipoRelacionado ?? idEquipo,
      observaciones: monitor.observaciones ?? "",
    });
    setShowMonitor(true);
  };

  const manejarTipoAsignacionMonitor = (tipo: TipoAsignacionMonitor) => {
    setTipoAsignacionMonitor(tipo);

    if (tipo === "EMPLEADO") {
      setMonitorForm((prev) => ({ ...prev, nombreResponsable: "" }));
      return;
    }

    if (tipo === "RESPONSABLE") {
      setMonitorForm((prev) => ({ ...prev, numEmpleadoAsignado: "" }));
      return;
    }

    setMonitorForm((prev) => ({
      ...prev,
      numEmpleadoAsignado: "",
      nombreResponsable: "",
    }));
  };

  const guardarMonitor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!monitorForm.idEstadoEquipo) return;

    setProcesando(true);

    try {
      const respuesta = await InventarioService.gestionarMonitor({
        ...monitorForm,
        idEquipoRelacionado: idEquipo,
        serviceTagSerial: monitorForm.serviceTagSerial?.trim() || null,
        numEmpleadoAsignado:
          tipoAsignacionMonitor === "EMPLEADO"
            ? monitorForm.numEmpleadoAsignado?.trim() || null
            : null,
        nombreResponsable:
          tipoAsignacionMonitor === "RESPONSABLE"
            ? monitorForm.nombreResponsable?.trim() || null
            : null,
        observaciones: monitorForm.observaciones?.trim() || null,
      });

      setShowMonitor(false);
      setMonitorEditar(null);
      onMensaje({
        encabezado: "Éxito",
        mensaje: respuesta.data.mensaje,
        color: "success",
      });
      await onRecargar();
    } catch (errorPeticion) {
      onMensaje({
        encabezado: "Error",
        mensaje: obtenerMensajeError(errorPeticion),
        color: "danger",
      });
    } finally {
      setProcesando(false);
    }
  };

  const eliminarMonitor = async () => {
    if (!confirmacion) return;

    setProcesando(true);

    try {
      const respuesta = await InventarioService.gestionarMonitor({
        accion: "ELIMINAR",
        idMonitor: confirmacion.id,
      });

      onMensaje({
        encabezado: "Éxito",
        mensaje: respuesta.data.mensaje,
        color: "success",
      });
      setConfirmacion(null);
      await onRecargar();
    } catch (errorPeticion) {
      onMensaje({
        encabezado: "Error",
        mensaje: obtenerMensajeError(errorPeticion),
        color: "danger",
      });
    } finally {
      setProcesando(false);
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white border-0 pt-3 d-flex align-items-center justify-content-between">
          <span className="fw-bold">
            <BsDisplay className="me-2 text-primary" />
            Monitores
          </span>

          <Button
            size="sm"
            variant="outline-primary"
            onClick={abrirNuevoMonitor}
          >
            <BsPlusCircle className="me-1" />
            Agregar
          </Button>
        </Card.Header>

        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th className="small">Serial</th>
                <th className="small">Estado</th>
                <th className="small">Asignado a </th>
                <th className="small">Observaciones</th>
                <th className="small">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {monitores.map((monitor) => (
                <tr key={monitor.idMonitor}>
                  <td className="font-monospace small">
                    {monitor.serviceTagSerial ?? "—"}
                  </td>
                  <td>
                    <Badge bg="success">
                      {monitor.estadoMonitor ?? "Activo"}
                    </Badge>
                  </td>
                  <td className="small">
                    {monitor.numEmpleadoAsignado ? (
                      <div>
                        <div className="fw-semibold">
                          {monitor.nombreEmpleado ?? monitor.numEmpleadoAsignado}
                        </div>
                        <div className="text-muted">
                          Empleado #{monitor.numEmpleadoAsignado}
                        </div>
                      </div>
                    ) : monitor.nombreResponsable ? (
                      <div>
                        <div className="fw-semibold">
                          {monitor.nombreResponsable}
                        </div>
                        <div className="text-muted">Responsable externo</div>
                      </div>
                    ) : (
                      <div>
                        <div className="fw-semibold text-muted">Sin asignar</div>
                        <div className="text-muted">Disponible</div>
                      </div>
                    )}
                  </td>
                  <td className="small">{monitor.observaciones ?? "—"}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => abrirEditarMonitor(monitor)}
                      >
                        <BsPencilSquare />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          setConfirmacion({
                            id: monitor.idMonitor,
                            descripcion:
                              monitor.serviceTagSerial ??
                              `Monitor ${monitor.idMonitor}`,
                          })
                        }
                      >
                        <BsTrash3 />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {monitores.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-muted">
                    Sin monitores relacionados.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showMonitor}
        onHide={() => {
          if (!procesando) {
            setShowMonitor(false);
            setMonitorEditar(null);
          }
        }}
        centered
        backdrop="static"
      >
        <Form onSubmit={guardarMonitor}>
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-3 fs-5">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 42,
                  height: 42,
                  color: "#0d6efd",
                  backgroundColor: "#e8f1ff",
                }}
              >
                <BsDisplay size={21} />
              </div>
              <div>
                <div className="fw-bold">
                  {monitorEditar ? "Editar monitor" : "Registrar monitor"}
                </div>
                <div className="text-muted fw-normal" style={{ fontSize: 12 }}>
                  Monitor relacionado con el equipo
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ backgroundColor: "#f8f9fa" }}>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Estado <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={monitorForm.idEstadoEquipo ?? ""}
                    onChange={(event) =>
                      setMonitorForm({
                        ...monitorForm,
                        idEstadoEquipo: Number(event.target.value) || null,
                      })
                    }
                    required
                  >
                    <option value="">Seleccione un estado</option>
                    {estadosEquipo.map((estado) => (
                      <option
                        key={estado.idEstadoEquipo}
                        value={estado.idEstadoEquipo}
                      >
                        {estado.estado}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Service Tag / Serial
                  </Form.Label>
                  <Form.Control
                    value={monitorForm.serviceTagSerial ?? ""}
                    onChange={(event) =>
                      setMonitorForm({
                        ...monitorForm,
                        serviceTagSerial: event.target.value,
                      })
                    }
                    placeholder="Serial del monitor"
                    maxLength={100}
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Tipo de asignación
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="radio"
                      id="monitor-empleado"
                      name="tipoAsignacionMonitor"
                      label="Empleado"
                      checked={tipoAsignacionMonitor === "EMPLEADO"}
                      onChange={() => manejarTipoAsignacionMonitor("EMPLEADO")}
                    />
                    <Form.Check
                      type="radio"
                      id="monitor-responsable"
                      name="tipoAsignacionMonitor"
                      label="Responsable externo"
                      checked={tipoAsignacionMonitor === "RESPONSABLE"}
                      onChange={() =>
                        manejarTipoAsignacionMonitor("RESPONSABLE")
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="monitor-sin-asignar"
                      name="tipoAsignacionMonitor"
                      label="Sin asignar"
                      checked={tipoAsignacionMonitor === "SIN_ASIGNAR"}
                      onChange={() =>
                        manejarTipoAsignacionMonitor("SIN_ASIGNAR")
                      }
                    />
                  </div>
                </Form.Group>
              </Col>

              {tipoAsignacionMonitor === "EMPLEADO" && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">
                      Número de empleado
                    </Form.Label>
                    <Form.Control
                      value={monitorForm.numEmpleadoAsignado ?? ""}
                      onChange={(event) =>
                        setMonitorForm((prev) => ({
                          ...prev,
                          numEmpleadoAsignado: event.target.value,
                        }))
                      }
                      placeholder="Ej. 010164"
                      maxLength={20}
                    />
                  </Form.Group>
                </Col>
              )}

              {tipoAsignacionMonitor === "RESPONSABLE" && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">
                      Nombre del responsable
                    </Form.Label>
                    <Form.Control
                      value={monitorForm.nombreResponsable ?? ""}
                      onChange={(event) =>
                        setMonitorForm((prev) => ({
                          ...prev,
                          nombreResponsable: event.target.value,
                        }))
                      }
                      placeholder="Ej. Patricia Guajardo"
                      maxLength={150}
                    />
                  </Form.Group>
                </Col>
              )}

              {tipoAsignacionMonitor === "SIN_ASIGNAR" && (
                <Col xs={12}>
                  <Alert variant="secondary" className="mb-0">
                    El monitor quedará sin empleado ni responsable asignado.
                  </Alert>
                </Col>
              )}

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Observaciones
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={monitorForm.observaciones ?? ""}
                    onChange={(event) =>
                      setMonitorForm({
                        ...monitorForm,
                        observaciones: event.target.value,
                      })
                    }
                    maxLength={500}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="light"
              disabled={procesando}
              onClick={() => {
                setShowMonitor(false);
                setMonitorEditar(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={procesando}>
              {procesando ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <BsSave className="me-2" />
                  Guardar monitor
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={confirmacion !== null}
        onHide={() => {
          if (!procesando) setConfirmacion(null);
        }}
        centered
      >
        <Modal.Header
          closeButton
          className="bg-danger text-white"
          closeVariant="white"
        >
          <Modal.Title className="fs-6 fw-bold">Confirmar baja</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          <div className="text-center py-3">
            <BsTrash3 size={36} className="text-danger mb-3" />
            <h5 className="fw-bold">¿Confirmas esta acción?</h5>
            <p className="mb-1 text-muted">Se dará de baja:</p>
            <p className="fw-bold">{confirmacion?.descripcion}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={procesando}
            onClick={() => setConfirmacion(null)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={procesando}
            onClick={() => void eliminarMonitor()}
          >
            {procesando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <BsTrash3 className="me-2" />
                Sí, dar de baja
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MonitoresEquipo;
